// Autosave der Meldung in der Schadenmappe (Spec §1): jede Änderung der
// Antworten/Schadensart wird nach 1.200 ms Ruhe gespeichert, bei Dauer-Tippen
// spätestens 4.000 ms nach der ersten ungespeicherten Änderung. Nur ein PUT
// gleichzeitig; Änderungen während des PUT folgen in genau einem weiteren PUT.
//
// Alles, was zu einer Id gehört, steckt in einer „Sitzung“. Ein Id-Wechsel
// schließt die alte Sitzung: Ihr Stand ist ab da eingefroren (der zuletzt für
// diese Id committete), eine ungespeicherte Änderung geht noch an die alte Id —
// bei laufendem PUT als dessen Folge-PUT, nie parallel. Antworten geschlossener
// Sitzungen ändern den angezeigten Zustand nicht mehr.
import * as React from "react";
import type { BigAnswers } from "../../schadenmeldung-big/types";
import type { DamagePatch } from "../types";

export interface AutosaveZustand {
  status: "idle" | "laeuft" | "ok" | "fehler";
  fehler: string | null;
}

/** Bereits auf dem Server liegender Stand einer Id (z. B. der POST der Anlage). */
export interface AutosaveBasis {
  damageId: number;
  answers: BigAnswers;
  schadensart: string | null;
}

/** Ruhezeit nach der letzten Änderung. */
const RUHE_MS = 1_200;
/** Höchstens so lange bleibt eine Änderung ungespeichert. */
const SPAETESTENS_MS = 4_000;

const IDLE: AutosaveZustand = { status: "idle", fehler: null };

interface Stand {
  answers: BigAnswers;
  schadensart: string | null;
  description: () => string;
}

interface Sitzung {
  id: number | null;
  /** Jüngster committeter Stand dieser Id — nur in Effekten geschrieben. */
  stand: Stand;
  /** Zuletzt gesehene Werte (Referenzvergleich); anfangs die Basis. */
  gesehen: { answers: BigAnswers; schadensart: string | null };
  ungespeichert: boolean;
  /** Läuft ein PUT, steht hier das Ende der ganzen Kette (inkl. Folge-PUT). */
  kette: Promise<void> | null;
  nochmal: boolean;
  geschlossen: boolean;
}

const patchAus = (s: Stand): DamagePatch => ({
  meldung: { ...s.answers, schadensart: s.schadensart },
  description: s.description(),
});

export function useMeldungAutosave(opts: {
  /** `null` vor der Anlage — dann wird nichts gespeichert. */
  damageId: number | null;
  answers: BigAnswers;
  schadensart: string | null;
  /** = `buildDescription(answers)`; erst beim Speichern ausgewertet. */
  description: () => string;
  /** = `updateDamage`. */
  speichern: (id: number, patch: DamagePatch) => Promise<unknown>;
  /**
   * Stand, der für `basis.damageId` schon auf dem Server liegt. Beim Binden an
   * diese Id gilt er als gespeichert; weicht der aktuelle Stand davon ab (z. B.
   * Eingaben während der Anlage), ist das eine ungespeicherte Änderung. Ohne
   * passende Basis gilt der Stand beim Binden als gespeichert (Laden ≠ Ändern).
   */
  basis?: AutosaveBasis | null;
}): {
  zustand: AutosaveZustand;
  /**
   * Speichert sofort (Schrittwechsel). Läuft gerade ein PUT, wird keiner
   * parallel gestartet: Das Promise erfüllt sich erst, wenn der laufende PUT und
   * — falls dieser Aufruf einen angehängt hat — der Folge-PUT mit dem jüngsten
   * Stand fertig sind. Lehnt nie ab; ein Fehler steht in `zustand`.
   */
  jetzt: () => Promise<void>;
  /** Schickt den jüngsten Stand erneut; ohne ungespeicherte Änderung ein No-op. */
  wiederholen: () => void;
} {
  const { damageId, answers, schadensart, description, basis } = opts;
  const [zustand, setZustand] = React.useState<AutosaveZustand>(IDLE);

  const speichernRef = React.useRef(opts.speichern);
  React.useEffect(() => {
    speichernRef.current = opts.speichern;
  });

  const sitzungRef = React.useRef<Sitzung>({
    id: null,
    stand: { answers, schadensart, description },
    gesehen: { answers, schadensart },
    ungespeichert: false,
    kette: null,
    nochmal: false,
    geschlossen: true,
  });
  const ruheTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const spaetestensTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const timerStoppen = React.useCallback(() => {
    if (ruheTimer.current !== null) clearTimeout(ruheTimer.current);
    if (spaetestensTimer.current !== null) clearTimeout(spaetestensTimer.current);
    ruheTimer.current = null;
    spaetestensTimer.current = null;
  }, []);

  /** Speichert die Sitzung — oder hängt einen Folge-PUT an den laufenden. */
  const starte = React.useCallback(
    (s: Sitzung): Promise<void> => {
      if (!s.geschlossen) timerStoppen();
      if (s.kette) {
        if (s.ungespeichert) s.nochmal = true;
        return s.kette;
      }
      const id = s.id;
      if (id === null || !s.ungespeichert) return Promise.resolve();

      let ende!: () => void;
      const kette = new Promise<void>((r) => {
        ende = r;
      });
      s.kette = kette;
      const melde = (z: AutosaveZustand) => {
        if (!s.geschlossen) setZustand(z);
      };
      void (async () => {
        try {
          // Pro Durchlauf ein PUT mit dem jüngsten Stand; ein in der Zwischenzeit
          // angefordertes „nochmal“ ergibt genau einen weiteren Durchlauf.
          for (;;) {
            s.ungespeichert = false;
            s.nochmal = false;
            melde({ status: "laeuft", fehler: null });
            try {
              await speichernRef.current(id, patchAus(s.stand));
            } catch (e: unknown) {
              s.ungespeichert = true; // Eingaben bleiben, `wiederholen` schickt sie
              s.nochmal = false;
              melde({ status: "fehler", fehler: e instanceof Error ? e.message : String(e) });
              return;
            }
            if (!s.nochmal) break;
          }
          // Eine Änderung während des PUT, deren Timer noch läuft, speichert der Timer.
          melde({ status: "ok", fehler: null });
        } finally {
          s.kette = null;
          ende();
        }
      })();
      return kette;
    },
    [timerStoppen],
  );

  // Id-Wechsel (und Verlassen): neue Sitzung; die alte wird geschlossen und
  // schickt eine noch ungespeicherte Änderung mit IHREM Stand an IHRE Id.
  React.useEffect(() => {
    const passendeBasis = basis && damageId !== null && basis.damageId === damageId ? basis : null;
    const s: Sitzung = {
      id: damageId,
      stand: { answers, schadensart, description },
      gesehen: passendeBasis
        ? { answers: passendeBasis.answers, schadensart: passendeBasis.schadensart }
        : { answers, schadensart },
      ungespeichert: false,
      kette: null,
      nochmal: false,
      geschlossen: false,
    };
    sitzungRef.current = s;
    setZustand(IDLE);
    return () => {
      s.geschlossen = true;
      timerStoppen();
      if (s.ungespeichert) void starte(s);
    };
    // Bewusst nur bei Id-Wechsel: Stand und Basis zählen nur beim Binden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [damageId, timerStoppen, starte]);

  // Nach jedem Commit: Stand der Sitzung nachführen und Änderungen erkennen
  // (Referenzvergleich). Der Stand wird nur hier geschrieben — so hält die
  // Sitzung beim Aufräumen nach einem Id-Wechsel noch den Stand ihrer Id.
  React.useEffect(() => {
    const s = sitzungRef.current;
    if (s.geschlossen) return;
    s.stand = { answers, schadensart, description };
    const alt = s.gesehen;
    s.gesehen = { answers, schadensart };
    if (s.id === null) return;
    if (alt.answers === answers && alt.schadensart === schadensart) return;
    s.ungespeichert = true;
    if (ruheTimer.current !== null) clearTimeout(ruheTimer.current);
    ruheTimer.current = setTimeout(() => void starte(s), RUHE_MS);
    if (spaetestensTimer.current === null) {
      spaetestensTimer.current = setTimeout(() => void starte(s), SPAETESTENS_MS);
    }
  });

  const jetzt = React.useCallback(() => starte(sitzungRef.current), [starte]);
  const wiederholen = React.useCallback(() => {
    void starte(sitzungRef.current);
  }, [starte]);

  return { zustand, jetzt, wiederholen };
}
